import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { stripeService } from '../../../services/stripeService';

const SubscriptionStatus = ({ userProfile, plans, onUpgrade }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  
  const currentTier = userProfile?.subscription_tier || 'free';
  const subscriptionStatus = userProfile?.subscription_status || 'inactive';
  const subscriptionEndDate = userProfile?.subscription_end_date;
  const scheduledDeletionDate = userProfile?.scheduled_deletion_date;
  const billingPeriod = userProfile?.subscription_billing_period || 'monthly';
  const isPilotMode = stripeService?.isPilotMode(userProfile);

  // Find the matching plan from the plans array
  let currentPlan = plans?.find(p => p?.tier === currentTier && p?.billing_period === billingPeriod);
  
  // If no matching plan found, construct a plan object based on tier and billing period
  if (!currentPlan) {
    if (currentTier === 'free') {
      currentPlan = {
        name: 'Free Plan',
        tier: 'free',
        price: 0,
        billing_period: 'monthly',
        features: { properties: 1, offers: 2, ai_features: false },
        property_limit: 1,
        offer_limit: 2,
        ai_features_enabled: false
      };
    } else if (currentTier === 'pro') {
      // Construct Pro plan name based on billing period
      const planName = billingPeriod === 'yearly' ? 'Pro Yearly' : 'Pro Monthly';
      const price = billingPeriod === 'yearly' ? 150 : 15;
      currentPlan = {
        name: planName,
        tier: 'pro',
        price: price,
        billing_period: billingPeriod,
        features: { properties: 'unlimited', offers: 'unlimited', ai_features: true },
        property_limit: null,
        offer_limit: null,
        ai_features_enabled: true
      };
    }
  }

  const statusConfig = {
    active: { label: 'Active', color: 'text-success', bgColor: 'bg-success/10', icon: 'CheckCircle' },
    inactive: { label: 'Inactive', color: 'text-muted-foreground', bgColor: 'bg-muted', icon: 'Circle' },
    past_due: { label: 'Past Due', color: 'text-warning', bgColor: 'bg-warning/10', icon: 'AlertCircle' },
    canceled: { label: 'Canceled', color: 'text-error', bgColor: 'bg-error/10', icon: 'XCircle' },
    trialing: { label: 'Trial', color: 'text-primary', bgColor: 'bg-primary/10', icon: 'Clock' }
  };

  const status = statusConfig?.[subscriptionStatus] || statusConfig?.inactive;

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? Your data will be deleted on your renewal date.')) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const { data, error: cancelError } = await stripeService?.cancelSubscription();

    if (cancelError) {
      setError(cancelError);
      setIsLoading(false);
      return;
    }

    // Refresh page to show updated status
    window.location?.reload();
  };

  const handleReactivateSubscription = async () => {
    setIsLoading(true);
    setError(null);

    const { data, error: reactivateError } = await stripeService?.reactivateSubscription();

    if (reactivateError) {
      setError(reactivateError);
      setIsLoading(false);
      return;
    }

    // Refresh page to show updated status
    window.location?.reload();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error rounded-lg">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {scheduledDeletionDate && subscriptionStatus === 'canceled' && (
        <div className="mb-4 p-4 bg-warning/10 border border-warning rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-warning mb-1">Subscription Canceled</h3>
              <p className="text-sm text-muted-foreground">
                Your subscription will end on {stripeService?.formatDate(subscriptionEndDate)}. 
                All your data will be permanently deleted on this date.
              </p>
              <button
                onClick={handleReactivateSubscription}
                disabled={isLoading}
                className="mt-3 text-sm font-medium text-primary hover:underline disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Reactivate Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Package" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{currentPlan?.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${status?.bgColor}`}>
                  <Icon name={status?.icon} size={12} className={status?.color} />
                  <span className={`text-xs font-medium ${status?.color}`}>{status?.label}</span>
                </div>
                {isPilotMode && (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-primary/10">
                    <Icon name="Rocket" size={12} className="text-primary" />
                    <span className="text-xs font-medium text-primary">Pilot Mode</span>
                  </div>
                )}
                {subscriptionEndDate && (
                  <span className="text-xs text-muted-foreground">
                    {isPilotMode ? 'Expires' : 'Renews'} {stripeService?.formatDate(subscriptionEndDate)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Icon name="Building2" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {currentPlan?.property_limit || 1} {currentPlan?.property_limit === 1 ? 'Property' : 'Properties'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="FileText" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {currentPlan?.offer_limit || 2} Offers per property
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={currentPlan?.ai_features_enabled ? "Check" : "X"} 
                size={16} 
                className={currentPlan?.ai_features_enabled ? "text-success" : "text-error"} 
              />
              <span className="text-sm text-muted-foreground">
                AI Features {currentPlan?.ai_features_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="text-right">
            <div className="text-3xl font-bold text-foreground">
              {stripeService?.formatAmount(currentPlan?.price || 0)}
            </div>
            <div className="text-sm text-muted-foreground">
              per {currentPlan?.billing_period === 'yearly' ? 'year' : 'month'}
            </div>
            {currentTier === 'pro' && currentPlan?.billing_period === 'yearly' && (
              <div className="text-xs text-success font-medium mt-1">
                Save 17% vs monthly
              </div>
            )}
          </div>
          {currentTier === 'free' && (
            <Button
              variant="default"
              size="lg"
              iconName="ArrowUpRight"
              iconPosition="right"
              onClick={onUpgrade}
            >
              Upgrade Plan
            </Button>
          )}
          {currentTier === 'pro' && subscriptionStatus === 'active' && (
            <Button
              variant="outline"
              size="lg"
              onClick={handleCancelSubscription}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Cancel Subscription'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatus;